import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { InputLogo } from "components/form/InputLogo"

import {
  AccountPageShell,
  Field,
  Footer,
  getEnterpriseAdditionalInitialState,
  isEnterpriseAdditionalDirty,
} from "./shared"
import { useUpdateEnterpriseAccount } from "./useUpdateEnterpriseAccount"

import styles from "./index.module.scss"

export const AdditionalInformationPage = () => {
  const { enterprise, isLoading, updateEnterprise } = useUpdateEnterpriseAccount()
  const initialValues = useMemo(() => getEnterpriseAdditionalInitialState(enterprise), [enterprise])
  const [additionalValues, setAdditionalValues] = useState(initialValues)
  const [isFormReady, setIsFormReady] = useState(false)

  const handleAdditionalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setAdditionalValues(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    const debounce = setTimeout(() => {
      setIsFormReady(Boolean(enterprise && isEnterpriseAdditionalDirty(enterprise, additionalValues)))
    }, 300)

    return () => {
      clearTimeout(debounce)
    }
  }, [enterprise, additionalValues])

  const handleSave = async () => {
    await updateEnterprise({ additionalValues })
  }

  return (
    <AccountPageShell footer={<Footer isLoading={isLoading} isSaveDisabled={!isFormReady} onSave={handleSave} />}>
      <h1 className={styles.title}>Additional Information</h1>
      <div className={styles.formGrid}>
        <Field label="Email" name="email" value={additionalValues.email} onChange={handleAdditionalChange} />
        <Field
          label="Enterprise web URL(optional)"
          name="enterpriseUrl"
          value={additionalValues.enterpriseUrl}
          onChange={handleAdditionalChange}
        />
        <Field
          label="Full name (optional)"
          name="fullName"
          value={additionalValues.fullName}
          placeholder="Enter full name"
          onChange={handleAdditionalChange}
        />
        <Field
          label="Phone number (optional)"
          name="phoneNumber"
          value={additionalValues.phoneNumber}
          onChange={handleAdditionalChange}
        />
        <div className={classNames(styles.field, styles.fieldNarrow)}>
          <InputLogo
            id="enterpriseLogo"
            name="enterpriseLogo"
            label="Enterprise logo (optional)"
            value={additionalValues.image ? [additionalValues.image] : null}
            imageUrl={enterprise?.logo || enterprise?.image}
            className={styles.enterpriseLogo}
            formatNote="(Format: Png Ratio: 2:1 Max size: 750x375px)"
            onSetFile={files => setAdditionalValues(prev => ({ ...prev, image: files?.[0] }))}
            onValidationError={errorMessage => toast.error(errorMessage)}
          />
        </div>
      </div>
    </AccountPageShell>
  )
}
